import {
  makeStyles,
  MessageBar,
  MessageBarBody,
  Subtitle2,
  tokens,
} from "@fluentui/react-components";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelectedTag } from "../../contexts/SelectedTagContext";
import { useTagsCopy } from "../../hooks/useTags";
import { CopyTagRequest, FlatVsaaTag } from "../../models/VsaaTag";
import {
  getParentIdFromAncestors,
  hasDuplicateNameAtSameLevel,
} from "../../services/TagService";
import { TreeTagPickerDialog } from "../tagpicker/TreeTagPickerDialog";

interface CopyTagsPanelProps {
  tagCollectionId: string;
  allTags: FlatVsaaTag[];
  onIsCopying: (isCopying: boolean) => void;
}

const useStyles = makeStyles({
  copyContainer: {
    display: "flex",
    flexDirection: "column",
  },
  copyTitle: {
    marginBottom: tokens.spacingVerticalM,
  },
});

const getFullAncestorPath = (tag: FlatVsaaTag): string[] => {
  if (!tag.ancestorIds) return [tag.id];
  const lastAncestor = tag.ancestorIds[tag.ancestorIds.length - 1];
  return lastAncestor === tag.id
    ? tag.ancestorIds
    : [...tag.ancestorIds, tag.id];
};

const CopyTagsPanel = (props: CopyTagsPanelProps) => {
  const { tagCollectionId, allTags, onIsCopying } = props;
  const tagContext = useSelectedTag();
  const classes = useStyles();
  const { mutateAsync: copyTag } = useTagsCopy(tagCollectionId);
  const { t } = useTranslation();
  const [duplicateError, setDuplicateError] = useState<{
    tagId: string;
    message: string;
  } | null>(null);
  const pendingCopyTarget = useRef<{
    title: string;
    parentId: string;
  } | null>(null);

  // Select the copied tag once it appears in the refetched data
  useEffect(() => {
    if (!pendingCopyTarget.current) return;

    const { title, parentId } = pendingCopyTarget.current;
    const copiedTag = allTags.find(
      (tag) => tag.title === title && tag.parentId === parentId
    );

    if (copiedTag) {
      pendingCopyTarget.current = null;
      tagContext?.setSelectedTag(copiedTag);
    }
  }, [allTags, tagContext]);

  const isDescendantOfSource = (
    targetTag: FlatVsaaTag,
    sourceTag: FlatVsaaTag
  ): boolean => {
    if (!targetTag.ancestorIds) return false;
    return targetTag.ancestorIds.includes(sourceTag.id);
  };

  const handlePickerSubmit = async (selectedTags: FlatVsaaTag[]) => {
    if (!tagContext?.selectedTag) return;
    const { selectedTag } = tagContext;
    const selectedParent = selectedTags[0] || null;

    // Validate target is not a descendant of source
    if (selectedParent && isDescendantOfSource(selectedParent, selectedTag)) {
      setDuplicateError({
        tagId: selectedTag.id,
        message: t("copyTargetDescendantError"),
      });
      return;
    }

    const targetParentId = selectedParent
      ? getParentIdFromAncestors(getFullAncestorPath(selectedParent))
      : undefined;

    const isDuplicate = hasDuplicateNameAtSameLevel(
      selectedTag.title,
      targetParentId,
      allTags
    );

    if (isDuplicate) {
      setDuplicateError({
        tagId: selectedTag.id,
        message: t("duplicateTagNameError"),
      });
      return;
    }

    setDuplicateError(null);

    if (!selectedParent) {
      return;
    }

    const sourceAncestorIds = getFullAncestorPath(selectedTag);
    const targetAncestorIds = getFullAncestorPath(selectedParent);

    const copyRequest: CopyTagRequest = {
      id: selectedTag.id,
      ancestorIds: sourceAncestorIds,
      targetAncestorIds: targetAncestorIds,
    };

    onIsCopying(true);
    try {
      await copyTag(copyRequest);
      pendingCopyTarget.current = {
        title: selectedTag.title,
        parentId: selectedParent.id,
      };
    } catch {
      setDuplicateError({
        tagId: selectedTag.id,
        message: t("copyTagError"),
      });
    } finally {
      onIsCopying(false);
    }
  };

  return (
    <div className={classes.copyContainer}>
      <Subtitle2 className={classes.copyTitle}>{t("copySection")}</Subtitle2>
      <div>
        <TreeTagPickerDialog
          tagCollectionId={tagCollectionId}
          label={t("copySectionTo")}
          multiValue={false}
          values={[]}
          allTags={allTags}
          onSubmit={handlePickerSubmit}
          copy={true}
        />
        {duplicateError &&
          duplicateError.tagId === tagContext?.selectedTag?.id && (
            <MessageBar intent="error">
              <MessageBarBody>{duplicateError.message}</MessageBarBody>
            </MessageBar>
          )}
      </div>
    </div>
  );
};

export default CopyTagsPanel;
